"use client";

import React, { useContext, useEffect } from "react";
import { usePathname } from "next/navigation";
import { FormContext } from "@/contexts/FormDataContext";
import { GlobalContext } from "@/contexts/SiteDataContext";
import { useSchema } from "@/contexts/FormSchemaContext";
import { useFormUtils } from "@/hooks/useFormUtils";
import { amplitudeTrackEvent } from "@/services/Amplitude";
import { getServiceSelectedForAnalytics } from "@/utils/Form/formServices";
import Alert from "@/components/atoms/Alert";
import Button from "@/components/atoms/Button";
import ServiceSelector from "@/components/Form/ServiceSelector";
import AddonsField from "../Fields/AddonsField";
import { AddonsData } from "../Fields/types";
import ReviewItem from "../ReviewItem";
import "./styles.scss";

const Summary = () => {
  const { formSchema: formSteps, formDetails } = useSchema();
  const { t } = useContext(GlobalContext).context;
  const {
    formData,
    setFormData,
    servicesDetails,
    setSelectedMethod,
    selectedMethod,
    proofOfTravelError,
  } = useContext(FormContext)!;
  const { handleSendReviewedForm } = useFormUtils();
  const formName = formDetails?.pdf?.name;
  const hasServices = servicesDetails.length > 0;
  const normalizedCountry =
    typeof formData.country === "string" ? formData.country.trim().toUpperCase() : "";
  const livesInUS = ["US", "USA", "UNITED STATES", "UNITED STATES OF AMERICA"].includes(
    normalizedCountry
  );
  const shouldShowServices = hasServices && livesInUS;

  // formDetails still refers to `pdf` property, despite it was renamed to product at the api level for not breaking
  // compatibility and not to generate unexpected side effects
  // TODO: rename pdf to product

  // Test AB PASGF-91
  const pathname = usePathname();
  const isTestAB = pathname.includes("/review/passport-renewal/alt");

  useEffect(() => {
    // When new services, maybe we need to change the default selected service
    if (selectedMethod !== "form") setSelectedMethod("form");
  }, []);

  useEffect(() => {
    if (!shouldShowServices && selectedMethod !== "form") {
      setSelectedMethod("form");
    }
  }, [selectedMethod, setSelectedMethod, shouldShowServices]);

  useEffect(() => {
    if (!formName || !selectedMethod) return;

    const timer = setTimeout(() => {
      // When new services, maybe we need to send different form name
      const formTypeName = formName;
      amplitudeTrackEvent("view_page_reviewPage", {
        form: formTypeName,
        service_selected: getServiceSelectedForAnalytics({ selectedMethod }),
      });
    }, 500);

    return () => clearTimeout(timer);
  }, [formName, selectedMethod]);

  if (!formSteps) return null;

  const handleAddonsOnChange = (addons: AddonsData[]) => {
    setFormData((prev: any) => ({ ...prev, addons: addons }));
    // TODO: See if we need to send the event here
  };

  return (
    <div className="Summary u-box">
      <h2 className="Summary__title">{t["Personal Application Review"]}</h2>
      <p className="Summary__subTitle">{t["Review the data below and make changes if needed"]}</p>

      {formSteps.map((step, stepIndex) => (
        <ReviewItem
          key={stepIndex}
          step={step}
          index={stepIndex}
          formSteps={formSteps}
          defaultOpen={(!hasServices || isTestAB) && stepIndex === 0}
        />
      ))}
      <div className={`disclaimer__wrapper`}>
        <Alert
          className="disclaimer__wrapper__alert"
          content={
            t["<b>Important:</b> Make sure all the data entered is correct before continue."]
          }
          showLeftIcon
        />
      </div>

      {shouldShowServices && <ServiceSelector />}

      <div className="review__addons">
        <AddonsField
          t={t}
          label={"Select Add-ons"}
          onChange={handleAddonsOnChange}
          addons={formData.addons}
          hideCardAndPriorityAddons={selectedMethod !== "form" && hasServices}
          value={[]}
        />
      </div>
      <div className="Summary__submit-button">
        <Button
          primary
          buttonId="submit-application"
          dataQa={`submit-application`}
          label={t["Continue"]}
          onClick={handleSendReviewedForm}
          disabled={proofOfTravelError}
        />
      </div>
    </div>
  );
};

export default Summary;
